import { useContext } from 'react';

import { StepperContext } from 'lib/context/StepperContext/StepperContext';

import StepperLayout from '../StepperLayout/StepperLayout';
import StepperOverlays from '../StepperOverlays/StepperOverlays';

/**
 * Renders the active stepper view (overlays + layout) once the wizard is loaded.
 * Reads core wizard state from StepperContext; takes view-only props directly.
 */
const StepperShell = ({
  // overlay state
  showTour,
  tourStepsConfig,
  onTourClose,
  showIntro,
  IntroComponent,
  consultant,
  currentUserId,
  isAuthenticated,
  setShowIntro,
  startTourIfUnseen,
  showTasksIntro,
  setShowTasksIntro,
  assignedTasks,
  setNavigateToCode,
  // layout shared
  isNarrowScreen,
  sidebarOpen,
  closeSidebar,
  toggleSidebar,
  isEnteringFromIntro,
  mainContentRef,
  wizardState,
  // sidebar
  SmartSuggestionsComponent,
  AssistanceCardComponent,
  LoginComponent,
  BottomSlot,
  onExit,
  exitConfig,
  phases,
  sidebarType,
  hiddenSubstepCodes,
  smartSuggestions,
  onExpandStep,
  onCollapseStep,
  onActivateStep,
  // header
  headerButtonProps,
  headerInfoTooltip,
  headerOptions,
  progressValue,
  shouldAnimateHeader,
  // footer
  footerConfig,
  delegations,
  // step
  ActiveComponent
}) => {
  const ctx = useContext(StepperContext);
  const {
    isAdmin,
    practice,
    activeSubstep,
    orchestratorData,
    componentStepConfig,
    restrictedStepsCodes,
    goNext,
    goPrev,
    canGoPrev,
    navigateToSubstep,
    setHeaderButtonProps,
    setHeaderInfoTooltip,
    setHeaderOptionsOverride,
    setFooterConfig,
    addFooterActions,
    wizardState: ctxWizardState
  } = ctx;
  const stepsState = ctxWizardState.steps;
  const instance = ctxWizardState.instance;

  const sidebarComponentProps = {
    SmartSuggestionsComponent,
    AssistanceCardComponent,
    LoginComponent,
    BottomSlot
  };

  const overlaysProps = {
    showTour,
    tourStepsConfig,
    onTourClose,
    showIntro,
    IntroComponent,
    consultant,
    wizardInstance: wizardState?.instance,
    currentUserId,
    isAuthenticated,
    isAdmin,
    setShowIntro,
    startTourIfUnseen,
    showTasksIntro,
    setShowTasksIntro,
    assignedTasks,
    setNavigateToCode
  };

  const headerProps = {
    headerButtonProps,
    headerOptions,
    headerInfoTooltip,
    setHeaderButtonProps,
    setHeaderInfoTooltip,
    setHeaderOptionsOverride,
    progressValue,
    shouldAnimateHeader,
    onToggleSidebar: toggleSidebar,
    onExit,
    exitConfig,
    hasLoginSlot: !!LoginComponent
  };

  const footerProps = {
    footerConfig,
    setFooterConfig,
    addFooterActions,
    delegations,
    canGoPrev
  };

  const stepProps = {
    ActiveComponent,
    activeSubstep,
    instance,
    stepsState,
    componentStepConfig,
    orchestratorData,
    navigateToSubstep,
    goNext,
    goPrev
  };

  const layoutProps = {
    stepsState,
    phases,
    sidebarComponentProps,
    sidebarType,
    hiddenSubstepCodes,
    restrictedStepsCodes,
    isNarrowScreen,
    sidebarOpen,
    closeSidebar,
    isEnteringFromIntro,
    smartSuggestions,
    isAdmin,
    practice,
    mainContentRef,
    wizardState,
    onExpandStep,
    onCollapseStep,
    onActivateStep,
    onNavigateSubstep: navigateToSubstep,
    headerProps,
    footerProps,
    stepProps
  };

  return (
    <>
      <StepperOverlays {...overlaysProps} />
      <StepperLayout {...layoutProps} />
    </>
  );
};

export default StepperShell;
